<script setup lang="ts">
import { RouterView, useRouter, useRoute } from 'vue-router';
import { useAuthStore } from './stores';
import LoginModal from '@features/auth/components/LoginModal.vue';
import AccountBlockedModal from '@features/auth/components/AccountBlockedModal.vue';
import CanDoModal from '@shared/ui/components/modals/CanDoModal.vue';
import ToastContainer from '@shared/ui/toast/ToastContainer.vue';
import ComingSoonModal from '@shared/ui/components/modals/ComingSoonModal.vue';
import TermsAndConditionsModal from '@shared/ui/components/modals/TermsAndConditionsModal.vue';
import CompleteProfileDataModal from '@features/profile/components/CompleteProfileDataModal.vue';
import { computed, onMounted, ref, watch } from 'vue';
import RedirectModal from '@shared/ui/components/modals/RedirectModal.vue';
import { socialService } from '@features/social/api/social.service';
import { useSocialStore } from '@features/social/store/social';
import { useBillingStore } from '@features/billing/store/billing';
import { requiresProfileCompletion } from '@features/profile/composables/profileCompletion';
import { useTenantStore } from '@features/tenant/store/tenant';
import { useTenantHead } from '@features/tenant/composables/useTenantHead';
import { resolveAppShell } from '@features/tenant/appShellRegistry';

const authStore = useAuthStore();
const socialStore = useSocialStore();
const billingStore = useBillingStore();
const tenantStore = useTenantStore();
const router = useRouter();
const route = useRoute();

useTenantHead();

const appShell = computed(() => resolveAppShell(tenantStore.launcherTheme)());

const isModalEmbeddedBrowser = ref(false);
const isIOS = ref(false);
const showTermsModal = ref(false);
const showCompleteProfileModal = ref(false);
const termsContent = ref('');
// Esta variable se utiliza para redirigir al usuario a un navegador
// externo cuando está dentro de un navegador embebido (mobile)
const currentUrl = ref(window.location.href);

// force actions again
async function handleCloseLoginModal() {
  if (authStore.redirectToHomeOnCloseLoginModal) {
    await router.push('/');
  }
}

async function handleCloseCanDoModal() {
  if (authStore.redirectToHomeOnCloseCanDoModal) {
    await router.push('/');
  }
}

const redirectToDefaultBrowser = () => {
  if (/Android/i.test(navigator.userAgent)) {
    isModalEmbeddedBrowser.value = true;
    isIOS.value = false;
  } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    isModalEmbeddedBrowser.value = true;
    isIOS.value = true;
  } else {
    window.location.href = currentUrl.value;
  }
};

async function handleAcceptTerms() {
  showTermsModal.value = false;
  if (authStore.currentPlayer?.identityMode === 'managed') {
    await checkProfileCompletion();
  }
}

function handleCompleteProfile() {
  showCompleteProfileModal.value = false;
}

async function checkProfileCompletion() {
  if (authStore.currentPlayer?.identityMode !== 'managed') return;

  // Wait for the social user to be loaded if it isn't yet
  if (!socialStore._socialUser) {
    await socialStore.getMyUser();
  }

  // If still not available after fetch, bail out
  const user = socialStore._socialUser;
  if (!user) return;

  if (requiresProfileCompletion(user, tenantStore.isDelegated)) {
    showCompleteProfileModal.value = true;
  }
}

watch(
  () =>
    [
      authStore.currentPlayer?.playerId ?? '',
      authStore.currentPlayer?.operationId ?? '',
      authStore.currentPlayer?.identityMode ?? '',
      tenantStore.status,
      tenantStore.operationId,
    ] as const,
  async ([
    playerId,
    operationId,
    identityMode,
    tenantStatus,
    tenantOperationId,
  ]) => {
    showTermsModal.value = false;
    showCompleteProfileModal.value = false;

    const isCurrentSession = () =>
      authStore.currentPlayer?.playerId === playerId &&
      authStore.currentPlayer.operationId === operationId &&
      authStore.currentPlayer.identityMode === identityMode &&
      tenantStore.status === tenantStatus &&
      tenantStore.operationId === tenantOperationId;

    const isReadyManagedPlayer =
      Boolean(playerId) &&
      identityMode === 'managed' &&
      tenantStatus === 'ready' &&
      operationId === tenantOperationId;

    if (!isReadyManagedPlayer) {
      billingStore.currentSubscription = null;
      billingStore.initialized = false;
      return;
    }

    if (!billingStore.initialized) {
      await billingStore.fetchCurrentSubscription();
      if (!isCurrentSession()) return;
    }

    // Always refresh the social user on a new login to avoid stale data
    // (e.g. Apple SSO creates a new user with status 201 — we need fresh data)
    await socialStore.refreshMyUser();
    if (!isCurrentSession()) return;

    try {
      const response = await socialService.getActiveTerms();
      if (!isCurrentSession()) return;
      const termsData = response.data;

      if (termsData && termsData.userMustReAccept) {
        termsContent.value = termsData.content;
        showTermsModal.value = true;
      } else {
        await checkProfileCompletion();
      }
    } catch (error) {
      await checkProfileCompletion();
    }
  },
  { immediate: true },
);

onMounted(() => {
  const isEmbeddedBrowser =
    /(Instagram|FBAN|FBAV|Twitter|TikTok|Snapchat)/i.test(navigator.userAgent);

  if (isEmbeddedBrowser) {
    window.dispatchEvent(new CustomEvent('embedded-browser-detected'));
    redirectToDefaultBrowser();
  }
});
</script>

<template>
  <div class="flex size-full min-h-screen flex-col bg-background pb-0">
    <component :is="appShell" :chromeless="Boolean(route.meta.hideNavbar)">
      <RouterView />
    </component>

    <LoginModal
      v-if="!isModalEmbeddedBrowser"
      v-model="authStore.showLoginModal"
      message="To access this section you need to be logged in."
      @on:close="handleCloseLoginModal"
    />

    <CanDoModal
      v-model="authStore.canDoModalProps.showCanDoModal"
      :title="authStore.canDoModalProps.canDoTitle"
      :message="authStore.canDoModalProps.canDoMessage"
      @on:close="handleCloseCanDoModal"
    />

    <ToastContainer />

    <ComingSoonModal v-model="authStore.showComingSoonModal" />

    <AccountBlockedModal v-model="authStore.showAccountBlockedModal" />

    <TermsAndConditionsModal
      v-model="showTermsModal"
      :content="termsContent"
      @accept="handleAcceptTerms"
    />
    <CompleteProfileDataModal
      v-model="showCompleteProfileModal"
      @save="handleCompleteProfile"
    />

    <RedirectModal
      v-model="isModalEmbeddedBrowser"
      :is-ios="isIOS"
      :url-android="`intent://${currentUrl.replace('https://', '')}/#Intent;scheme=https;end`"
      title="Redirecting to the default browser"
      message="To continue correctly, please open this page in an external browser."
    />
  </div>
</template>
